import { Injectable } from '@angular/core';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { switchMap, map, catchError, finalize, take, filter } from 'rxjs/operators';
import * as TnaTemplateActions from './tna-templates.actions';
import { TTnaTemplatesActionType } from './tna-templates.models';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TPlani8State } from '../reducers';
import { ExcelUtilClass } from '@monorepo/utils';
import { TnaTemplatesFacade } from './tna-templates.facade';

@Injectable({ providedIn: 'root' })
export class TnaTemplateEffects {
  constructor(
    private actions$: Actions,
    private plani8DataService: Plani8DataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private router: Router,
    private store: Store<TPlani8State>,
    private excelHelper: ExcelUtilClass,
    private tnaTemplatesFacade: TnaTemplatesFacade,
  ) {}

  getTnaTemplateList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaTemplatesActionType.GetTnaTemplateList),
      switchMap((action: TnaTemplateActions.GetTnaTemplateList) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.fetchTnaTemplateList(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new TnaTemplateActions.GetTnaTemplateListSuccess(res)),
          catchError(() => {
            return of(new TnaTemplateActions.GetTnaTemplateListFailure());
          }),
        ),
      ),
    ),
  );

  updateTnaTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaTemplatesActionType.UpdateTnaTemplate),
      switchMap((action: TnaTemplateActions.UpdateTnaTemplate) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updateTnaTemplate(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new TnaTemplateActions.GetTnaTemplateList(action.fetchTnATemplatesPayload);
          }),
          catchError(() => {
            return of(new TnaTemplateActions.UpdateTnaTemplateFailure());
          }),
        ),
      ),
    ),
  );

  deleteTnaTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaTemplatesActionType.DeleteTnaTemplate),
      switchMap((action: TnaTemplateActions.DeleteTnaTemplate) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.deleteTnaTemplate(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new TnaTemplateActions.GetTnaTemplateList(action.fetchTnATemplatesPayload);
          }),
          catchError(() => {
            return of(new TnaTemplateActions.DeleteTnaTemplateFailure());
          }),
        ),
      ),
    ),
  );

  copyTnaTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaTemplatesActionType.CopyTnaTemplate),
      switchMap((action: TnaTemplateActions.CopyTnaTemplate) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.copyTnaTemplate(action.templateId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
            this.router.navigate(['/tna/tna-templates']);
          }),
          filter(() => !!action.fetchTnATemplatesPayload),
          map(() => {
            return new TnaTemplateActions.GetTnaTemplateList(action.fetchTnATemplatesPayload!);
          }),
          catchError(() => {
            return of(new TnaTemplateActions.CopyTnaTemplateFailure());
          }),
        ),
      ),
    ),
  );

  createTnATemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaTemplatesActionType.CreateTnATemplate),
      switchMap((action: TnaTemplateActions.CreateTnATemplate) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.createTnATemplate(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.router.navigate(['/tna/tna-details'], {
              queryParams: { templateId: res.id, templateName: action.payload.name },
            });
            return new TnaTemplateActions.CreateTnATemplateSuccess();
          }),
          catchError(() => {
            return of(new TnaTemplateActions.CreateTnATemplateFailure());
          }),
        ),
      ),
    ),
  );

  getTnADefaultSteps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaTemplatesActionType.GetTnaDefaultSteps),
      switchMap((action: TnaTemplateActions.GetTnaDefaultSteps) => {
        if (action.showLocalLoader) {
          this.tnaTemplatesFacade.setTnaDefaultStepsLoader(true);
          return of([null, action] as const);
        } else {
          return from(this.appNotificationUtilClass.presentLoading()).pipe(
            map((loaderEl) => {
              return [loaderEl, action] as const;
            }),
          );
        }
      }),
      switchMap(([loaderEl, action]) => {
        return this.plani8DataService.getTnaDefaultSteps().pipe(
          finalize(() => {
            action.showLocalLoader
              ? this.tnaTemplatesFacade.setTnaDefaultStepsLoader(false)
              : loaderEl && this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new TnaTemplateActions.GetTnaDefaultStepsSuccess(res);
          }),
          catchError(() => {
            return of(new TnaTemplateActions.GetTnaDefaultStepsFailure());
          }),
        );
      }),
    ),
  );

  getTnaTemplateDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaTemplatesActionType.GetTnaTemplateDetails),
      switchMap((action: TnaTemplateActions.GetTnaTemplateDetails) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) => {
        return this.plani8DataService.getTnATemplateDetails(action.templateName).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new TnaTemplateActions.GetTnaTemplateDetailsSuccess(res);
          }),
          catchError(() => of(new TnaTemplateActions.GetTnaTemplateDetailsFailure())),
        );
      }),
    ),
  );

  addEditTnATemplateStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaTemplatesActionType.AddEditTnaTemplateStep),
      switchMap((action: TnaTemplateActions.AddEditTnaTemplateStep) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.addEditTnATemplateStep(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.router.navigate(['/tna/tna-details'], {
              queryParams: { templateId: res.id, templateName: action.templateName },
            });
            return new TnaTemplateActions.AddEditTnaTemplateStepSuccess();
          }),
          catchError(() => {
            return of(new TnaTemplateActions.AddEditTnaTemplateStepFailure());
          }),
        ),
      ),
    ),
  );

  deleteTnATemplateStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaTemplatesActionType.DeleteTnaTemplateStep),
      switchMap((action: TnaTemplateActions.DeleteTnaTemplateStep) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.deleteTnaTemplateStep(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => new TnaTemplateActions.GetTnaTemplateDetails(action.templateName)),
          catchError(() => {
            return of(new TnaTemplateActions.DeleteTnaTemplateFailure());
          }),
        ),
      ),
    ),
  );

  downloadTnaTemplateDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaTemplatesActionType.DownloadTnaTemplateDetails),
      switchMap((action: TnaTemplateActions.DownloadTnaTemplateDetails) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) => {
        return this.plani8DataService.downloadTnaDetails(action.templateId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.excelHelper.downloadFile(res, 'tna-template-details.xlsx');
            return new TnaTemplateActions.DownloadTnaTemplateDetailsSuccess();
          }),
          catchError(() => of(new TnaTemplateActions.DownloadTnaTemplateDetailsFailure())),
        );
      }),
    ),
  );

  assignTnaToOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaTemplatesActionType.AssignTnaToOrder),
      switchMap((action: TnaTemplateActions.AssignTnaToOrder) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.assignTnaToOrders(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.router.navigate(['/orders/list']);
            this.appNotificationUtilClass.presentToast('Tna allocation to order(s) is complete.');
            return new TnaTemplateActions.AssignTnaToOrderSuccess();
          }),
          catchError(() => {
            return of(new TnaTemplateActions.AssignTnaToOrderFailure());
          }),
        ),
      ),
    ),
  );
}
